import { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Check, CheckCircle2, Copy } from 'lucide-react';
import { PillButton } from '@ab-platform/ui/pill-button';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { readLastRequestId } from '@/lib/onboarding-success-nav';

interface SuccessPageState {
  submissionId?: string;
}

// Public marketing/home site. Env-overridable so a domain change is a config
// edit, not a code change. PR-ONBOARDING-SUCCESS-UX-1.
const HOME_URL =
  (import.meta.env.VITE_PUBLIC_HOME_URL as string | undefined) ??
  'https://www.aurinobroker.com';

export function SuccessPage(): JSX.Element {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = (location.state as SuccessPageState | null) ?? null;
  // PR-ONBOARDING-SUCCESS-REF-PERSIST-1 — resolve the reference id so it
  // survives a hard refresh (router state is wiped on reload):
  //   1. ?requestId= query param   2. router state   3. sessionStorage mirror
  const submissionId =
    searchParams.get('requestId') ?? state?.submissionId ?? readLastRequestId();
  const [copied, setCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    if (!submissionId) return;
    try {
      await navigator.clipboard.writeText(submissionId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable / blocked — leave the ID visible to copy by hand.
    }
  };

  return (
    <FocusedPageShell
      title="Richiesta inviata correttamente"
      description="Verificheremo i dati e ti contatteremo via email per i prossimi passaggi."
      footer={
        <PillButton asChild size="lg" className="w-full">
          <a href={HOME_URL}>Torna alla Home</a>
        </PillButton>
      }
    >
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <span
          className="flex h-14 w-14 items-center justify-center rounded-full bg-state-success/10 text-state-success"
          aria-hidden
        >
          <CheckCircle2 className="h-7 w-7" />
        </span>

        {submissionId ? (
          <div className="mt-2 grid gap-1.5 text-center">
            <span className="text-xs text-muted-foreground">
              Riferimento richiesta
            </span>
            <div className="flex items-center gap-2 rounded-md border border-foreground/10 bg-background px-3 py-2">
              <span className="min-w-0 flex-1 break-all font-mono text-sm text-foreground">
                {submissionId}
              </span>
              <PillButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void handleCopy()}
                aria-label="Copia riferimento richiesta"
              >
                {copied ? (
                  <Check className="h-4 w-4" aria-hidden />
                ) : (
                  <Copy className="h-4 w-4" aria-hidden />
                )}
              </PillButton>
            </div>
            <span className="text-xs text-muted-foreground">
              Conserva questo ID se devi contattare l’assistenza.
            </span>
          </div>
        ) : null}
      </div>
    </FocusedPageShell>
  );
}
